import { useEffect } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { Layout } from '@/components/Layout'
import { Dashboard } from '@/pages/Dashboard'
import { RepoDetail } from '@/pages/RepoDetail'
import { AssayPanel } from '@/pages/AssayPanel'
import { ConclavePanel } from '@/pages/ConclavePanel'
import { Services } from '@/pages/Services'
import { Settings } from '@/pages/Settings'
import { GateCallback } from '@/pages/GateCallback'
import { useAuth } from '@/store/auth'
import { useUI } from '@/store/ui'
import { buildGateLoginUrl, callbackRedirectUri, makeState, rememberState } from '@/lib/gateSso'

/** Validates the session cookie once on mount (via /auth/me) before routing. */
function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuth((s) => s.status)
  const loadMe = useAuth((s) => s.loadMe)
  useEffect(() => {
    loadMe()
  }, [loadMe])
  if (status === 'loading') return <p className="ap-empty">Loading…</p>
  return <>{children}</>
}

/** The invisible standard (2026-08-04): there is no login page here. An
 *  anonymous visitor is SILENTLY sent to the gate and returned signed in.
 *
 *  Two mechanisms, chosen by whether the backend advertises a gate client id
 *  (code-handoff on):
 *  - code-handoff (gateClientId present, Phase 0a): redirect with Apex's
 *    client_id + registered /auth/callback + a one-time state; the gate returns
 *    a code the Daemon redeems for its OWN apex_session cookie. Survives the
 *    gate's move to diwata.id.
 *  - legacy (no gateClientId): the plain-`next` bounce that reads the shared
 *    .diwata.domains cookie — only works same-registrable-domain. */
function RequireAuth() {
  const status = useAuth((s) => s.status)
  const gateOrigin = useAuth((s) => s.gateOrigin)
  const gateClientId = useAuth((s) => s.gateClientId)
  const gateUrl = useUI((s) => s.gateUrl)

  useEffect(() => {
    if (status !== 'anon') return
    if (gateOrigin && gateClientId) {
      // Code-handoff: park a fresh state (CSRF binding) + where we were headed,
      // then hand off to the gate with our client id and registered callback.
      const state = makeState()
      const from = window.location.pathname + window.location.search + window.location.hash
      rememberState(sessionStorage, state, from)
      const redirectUri = callbackRedirectUri(window.location.origin)
      window.location.assign(buildGateLoginUrl(gateOrigin, gateClientId, redirectUri, state))
    } else {
      // Legacy shared-cookie bounce. The gate origin from /auth/gate/status wins;
      // fall back to the configured gateUrl so a backend that can't report it
      // still bounces (today's behaviour).
      const base = (gateOrigin ?? gateUrl).replace(/\/+$/, '')
      const next = encodeURIComponent(window.location.href)
      window.location.assign(`${base}/login?next=${next}`)
    }
  }, [status, gateOrigin, gateClientId, gateUrl])

  if (status === 'authed') return <Outlet />
  return null // loading, or mid-bounce to the gate
}

export default function App() {
  return (
    <AuthGate>
      <Routes>
        {/* The gate's registered landing — public (no session yet): it redeems
            the one-time code, then routes on. Must sit OUTSIDE RequireAuth or it
            would bounce straight back to the gate. */}
        <Route path="auth/callback" element={<GateCallback />} />
        <Route element={<RequireAuth />}>
          <Route element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="repos/:name" element={<RepoDetail />} />
            <Route path="assay"    element={<AssayPanel />} />
            <Route path="conclave" element={<ConclavePanel />} />
            <Route path="services" element={<Services />} />
            <Route path="settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Routes>
    </AuthGate>
  )
}
